// Client-side duration estimates (ms) for the server-side processing phase
// between stage milestones. Used only to animate the progress bar smoothly
// between real stage-completion signals from DatoCMS — never shown to users
// as literal times.
//
// Env overrides are read at build time (CRA inlines REACT_APP_* values into
// the bundle), so tuning these requires updating the env vars and redeploying.
// Once we have Datadog data, adjust the defaults or env values and ship a new build.

type StageKey = 'startToOptimized' | 'optimizedToAssets' | 'assetsToReady';

function readNumberEnv(key: string, fallback: number): number {
  const raw = process.env[key];
  if (!raw) return fallback;
  const num = Number(raw);
  return Number.isFinite(num) && num >= 0 ? num : fallback;
}

const BASE_START_TO_OPTIMIZED_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_START_TO_OPTIMIZED_MS',
  30_000,
);
const BASE_OPTIMIZED_TO_ASSETS_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_OPTIMIZED_TO_ASSETS_MS',
  45_000,
);
const BASE_ASSETS_TO_READY_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_ASSETS_TO_READY_MS',
  15_000,
);

const PER_MB_START_TO_OPTIMIZED_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_PER_MB_MS',
  500,
);
const PER_SEC_OPTIMIZED_TO_ASSETS_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_PER_VIDEO_SEC_MS',
  1_500,
);
const FALLBACK_VIDEO_DURATION_SEC = 60;
// Used only when fileSizeBytes is unavailable (e.g., user reloaded mid-
// processing and we lost the in-memory upload phase). Picked to be more
// generous than a tiny test clip so the bar doesn't asymptote in 30s.
const FALLBACK_FILE_SIZE_MB = 200;

export interface StageEstimateInputs {
  videoDurationSec?: number;
  fileSizeBytes?: number;
}

export function estimateStageDurationMs(
  stage: StageKey,
  inputs: StageEstimateInputs,
): number {
  const { videoDurationSec, fileSizeBytes } = inputs;
  const fileSizeMB =
    fileSizeBytes !== undefined
      ? fileSizeBytes / (1024 * 1024)
      : FALLBACK_FILE_SIZE_MB;
  const videoSec = videoDurationSec ?? FALLBACK_VIDEO_DURATION_SEC;

  switch (stage) {
    case 'startToOptimized':
      return (
        BASE_START_TO_OPTIMIZED_MS + fileSizeMB * PER_MB_START_TO_OPTIMIZED_MS
      );
    case 'optimizedToAssets':
      return (
        BASE_OPTIMIZED_TO_ASSETS_MS + videoSec * PER_SEC_OPTIMIZED_TO_ASSETS_MS
      );
    case 'assetsToReady':
      return BASE_ASSETS_TO_READY_MS;
  }
}

// --- Publish render (PLAT-10707) -------------------------------------------

// Halved from 45_000 / 2_000 (PLAT-11209). Those were never checked against a
// real render, because the caller was feeding them a story-length duration —
// the two errors partly cancelled. Against an actual clip length they
// overpredicted by ~2x: a render finishing at half its predicted ETA left the
// progress bar at 67% before it jumped to 100.
const BASE_PUBLISH_RENDER_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_PUBLISH_RENDER_MS',
  22_500,
);
const PER_SEC_PUBLISH_RENDER_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_PUBLISH_PER_VIDEO_SEC_MS',
  1_000,
);
// A clip, not a whole story — deliberately separate from
// FALLBACK_VIDEO_DURATION_SEC so a missing clipJson.duration can't silently
// reintroduce a story-length estimate (see the doc comment below).
const FALLBACK_CLIP_DURATION_SEC = 30;

/**
 * Expected wall-clock for one publish variant's server render (Creatomate
 * dispatch → succeeded). Drives the publish drawer's interpolated progress
 * bar between the discrete `rendering` → terminal status pushes — the
 * backend exposes no percentage, so this is the smoothing model.
 *
 * `clipDurationSec` is the duration of the *rendered output* — the clip's
 * timeline length (`clipJson.duration`), NOT the story's source video. Passing
 * the source duration was the original PLAT-10707 defect (PLAT-11209): a 40s
 * clip cut from a 30-minute interview produced a ~61-minute ETA, so the bar
 * crawled to ~8% and then snapped to 100%.
 */
export function estimatePublishRenderMs(inputs: {
  clipDurationSec?: number;
}): number {
  const clipSec = inputs.clipDurationSec ?? FALLBACK_CLIP_DURATION_SEC;
  return BASE_PUBLISH_RENDER_MS + clipSec * PER_SEC_PUBLISH_RENDER_MS;
}

// --- AI dub generation (PLAT-10761) ----------------------------------------

const BASE_DUB_GENERATE_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_DUB_GENERATE_MS',
  30_000,
);
const PER_SEC_DUB_GENERATE_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_DUB_PER_VIDEO_SEC_MS',
  2_500,
);
// Multi-speaker runs fan out to separate provider syntheses; add a modest
// per-extra-speaker term on top of the (speaker-independent) transcript length.
const PER_EXTRA_SPEAKER_DUB_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_DUB_PER_SPEAKER_MS',
  8_000,
);

/**
 * Expected wall-clock for a whole-story AI dub synthesis. The engine exposes no
 * percentage (a single blocking request → coverage), so this drives the dub
 * modal's interpolated progress bar the same way `estimatePublishRenderMs`
 * smooths the publish drawer.
 */
export function estimateDubGenerateMs(inputs: {
  videoDurationSec?: number;
  speakerCount?: number;
}): number {
  const videoSec = inputs.videoDurationSec ?? FALLBACK_VIDEO_DURATION_SEC;
  const speakers = Math.max(1, inputs.speakerCount ?? 1);
  return (
    BASE_DUB_GENERATE_MS +
    videoSec * PER_SEC_DUB_GENERATE_MS +
    (speakers - 1) * PER_EXTRA_SPEAKER_DUB_MS
  );
}

// --- Voice / music separation (PLAT-11563) ----------------------------------

// The stem splitter processes the whole story track in one request, so the
// wait scales with the source length. Untuned initial guesses (no Datadog data
// yet): a short fixed startup plus under a second per second of audio.
const BASE_STEM_SEPARATION_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_STEM_SEPARATION_MS',
  15_000,
);
const PER_SEC_STEM_SEPARATION_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_STEM_SEPARATION_PER_VIDEO_SEC_MS',
  800,
);

export function estimateStemSeparationMs(inputs: {
  videoDurationSec?: number | null;
}): number {
  const videoSec = inputs.videoDurationSec ?? FALLBACK_VIDEO_DURATION_SEC;
  return BASE_STEM_SEPARATION_MS + videoSec * PER_SEC_STEM_SEPARATION_MS;
}

// --- Transcript translation (PLAT-11690) ------------------------------------

// Keyed on TEXT, not on runtime: a translation's cost is the number of words an
// LLM has to read and write, and a sparse thirty-minute interview is nothing
// like a thirty-minute monologue of the same length. Composition targets are a
// second pass the backend runs alongside the transcript one, so they add their
// own per-target term rather than scaling the base.
//
// Untuned initial guesses, as `estimateStemSeparationMs` still is: these land a
// typical story near the minute the generate popover has always promised. Worth
// revisiting once there is Datadog data.
const BASE_TRANSLATE_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_TRANSLATE_MS',
  20_000,
);
const PER_ELEMENT_TRANSLATE_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_TRANSLATE_PER_ELEMENT_MS',
  25,
);
const PER_COMPOSITION_TARGET_TRANSLATE_MS = readNumberEnv(
  'REACT_APP_ESTIMATE_TRANSLATE_PER_COMPOSITION_MS',
  150,
);

/**
 * Expected wall-clock for one language's transcript translation. The endpoint
 * is a single blocking request with no percentage of its own, so this drives
 * the on-page run chip's interpolated progress the way `estimateDubGenerateMs`
 * drives the dub's.
 */
export function estimateTranslateMs(inputs: {
  transcriptElementCount?: number | null;
  compositionTargetCount?: number | null;
}): number {
  const elements = inputs.transcriptElementCount ?? 0;
  const targets = inputs.compositionTargetCount ?? 0;
  return (
    BASE_TRANSLATE_MS +
    elements * PER_ELEMENT_TRANSLATE_MS +
    targets * PER_COMPOSITION_TARGET_TRANSLATE_MS
  );
}
